import { DOCUMENT } from '@angular/common';
import {
    Component,
    computed,
    inject,
    input,
    OnDestroy,
    OnInit,
    output,
    signal,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { customDateFormat, getDateFormatHint } from '@app/helpers/date/date-helper';
import { vendorReviewQrForm } from '@app/modules/user-management/forms/vendor-supplier-onboarding/review-qr/review-qr.form';
import { DataTestIdPipe } from '@app/pipes/data-test-id/data-test-id-pipe';
import { AppStore } from '@app/stores/app/app.store';
import { CompanyStore } from '@app/stores/company/company.store';
import { IDynamicActions } from '@app/types/dynamic-actions';
import { QRCodeComponent } from 'angularx-qrcode';
import { delay, of, Subscription } from 'rxjs';
import { DynamicActionsComponent } from '../../dynamic-actions/dynamic-actions.component';

@Component({
    selector: 'app-review-qr-details',
    imports: [
        MatFormFieldModule,
        FormsModule,
        MatInput,
        MatLabel,
        MatDatepickerModule,
        MatIconModule,
        MatButtonModule,
        ReactiveFormsModule,
        MatTooltipModule,
        QRCodeComponent,
        DataTestIdPipe,
        DynamicActionsComponent,
    ],
    providers: [
        { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
        { provide: MAT_DATE_FORMATS, useValue: customDateFormat() },
    ],
    templateUrl: './review-qr-details.component.html',
    styleUrl: './review-qr-details.component.scss',
})
export class ReviewQrDetailsComponent implements OnInit, OnDestroy {
    private appStore = inject(AppStore);
    translate = this.appStore.i18n;
    fg = vendorReviewQrForm();
    minDate = new Date();
    private document = inject(DOCUMENT).defaultView?.document;
    confirmClose = output();
    store = inject(CompanyStore);
    qrDetails = computed(() => this.store.qrDetails());
    id = computed(() => this.store.id());
    subs$ = new Subscription();
    state = signal({
        dateFormat: getDateFormatHint(),
    });
    dateFormat = computed(() => this.state().dateFormat);

    actions = input.required<IDynamicActions[]>();
    testIdPrefix = input.required<string>();
    isReadOnly = input<boolean>(false);

    ngOnInit(): void {
        const { expirationDate, qrLink, name } = this.qrDetails();

        this.fg.patchValue({
            qrCodeUrl: qrLink,
            name: name,
            expirationDate: expirationDate,
        });

        if (this.isReadOnly()) {
            this.fg.disable();
        }
    }

    ngOnDestroy(): void {
        this.subs$.unsubscribe();
    }

    async copyQrCodeUrl(callBack: MatTooltip): Promise<void> {
        const qrCodeUrlValue = this.fg.controls.qrCodeUrl.value;
        await navigator.clipboard.writeText(qrCodeUrlValue);

        callBack.disabled = false;
        callBack.show();
        void of(null)
            .pipe(delay(1000))
            .subscribe(() => {
                callBack.disabled = true;
            });
    }

    async copyQrCode(callBack: MatTooltip): Promise<void> {
        if (!this.document || typeof ClipboardItem === 'undefined') return;
        const imgSrc = this.document
            .querySelector('#review-qr-code img')
            ?.getAttribute('src');

        if (imgSrc) {
            const imageData = async (): Promise<Blob> => {
                const response = await fetch(imgSrc);
                return await response.blob();
            };

            await navigator.clipboard.write([
                new ClipboardItem({ 'image/png': imageData() }),
            ]);

            callBack.disabled = false;
            callBack.show();

            void of(null)
                .pipe(delay(1000))
                .subscribe(() => {
                    callBack.disabled = true;
                });
        }
    }

    downloadQrCode(): void {
        if (!this.document) return;
        const imgSrc = this.document
            .querySelector('#review-qr-code img')
            ?.getAttribute('src');

        if (imgSrc) {
            const fileName = this.qrDetails().name.toLowerCase().replaceAll(/ /g, '-');
            const link = this.document.createElement('a');
            link.href = imgSrc;
            link.download = `${fileName}-qr-code.png`;
            link.click();
        }
    }
}
